import { format, isToday, setHours, setMinutes, isValid } from "date-fns";
import { Calendar2 } from "iconsax-react";
import React, { useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import { cn } from "src/lib/utils";
import { Calendar, CalendarPickerMode, CalendarProps } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

import TimePicker from "./TimePicker";

interface DateRangePickerProps {
  date?: Date | DateRange | undefined;
  setDate?:
    | React.Dispatch<React.SetStateAction<Date | undefined>>
    | React.Dispatch<React.SetStateAction<DateRange | undefined>>;
  placeholder: string;
  type: "SINGLE" | "RANGE";
  className?: string;
  disabled?: boolean;
  minDate?: Date | null;
  maxDate?: Date | null;
  isIncludeTime?: boolean;
  pickerMode?: CalendarPickerMode;
  autoPreFill?: boolean;
  iconPosition?: "left" | "right";
  isShowNoEndDate?: boolean;
  disableNavViewChange?: boolean;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  date,
  setDate,
  placeholder,
  type,
  className,
  disabled = false,
  minDate,
  maxDate,
  isIncludeTime,
  pickerMode: initialPickerMode,
  autoPreFill,
  iconPosition = "right",
  isShowNoEndDate,
  disableNavViewChange,
}) => {
  const selectedDate = date as Date;
  const selectedDateRange = date as DateRange;

  const setDatePicker = setDate as React.Dispatch<
    React.SetStateAction<Date | undefined>
  >;
  const setDateRangePicker = setDate as React.Dispatch<
    React.SetStateAction<DateRange | undefined>
  >;

  const [isShow, setIsShow] = useState(false);
  const [pickerMode, setPickerMode] = useState<CalendarPickerMode>(
    initialPickerMode || "days"
  );

  useEffect(() => {
    setPickerMode(initialPickerMode || "days");
  }, [initialPickerMode]);

  const [timeValue, setTimeValue] = useState<string>(
    type === "SINGLE" && selectedDate && isValid(selectedDate)
      ? format(selectedDate, "HH:mm")
      : "00:00"
  );

  const isMonthMode = initialPickerMode === "months";
  const isYearMode = initialPickerMode === "years";

  const labelDateFormat = isMonthMode
    ? "MMMM yyyy"
    : isYearMode
    ? "yyyy"
    : "dd MMMM yyyy";

  const calendarType: CalendarProps =
    type === "SINGLE"
      ? {
          mode: "single",
          selected:
            selectedDate && isValid(selectedDate) ? selectedDate : undefined,
          onSelect: (date) => {
            if (date && isValid(date)) {
              if (isIncludeTime) {
                const [hours, minutes] = timeValue
                  .split(":")
                  .map((str) => parseInt(str, 10));
                const newSelectedDate = setHours(
                  setMinutes(date as Date, minutes),
                  hours
                );

                if (!selectedDate && isToday(date)) {
                  setTimeValue(format(new Date(), "HH:mm"));
                }
                setDatePicker(newSelectedDate);
              } else {
                setDatePicker(date);
                // Only close when in days mode (final selection)
                if (pickerMode === "days") {
                  setIsShow(false);
                }
              }
            } else {
              setDatePicker(undefined);
            }
          },
        }
      : {
          mode: "range",
          onSelect: (dateRange) => {
            setDateRangePicker(dateRange);

            if (dateRange?.to) {
              setIsShow(false);
            }
          },
          defaultMonth: selectedDateRange?.from,
          selected: selectedDateRange,
          numberOfMonths: 2,
        };

  return (
    <Popover
      modal
      open={isShow}
      onOpenChange={(open) => {
        setIsShow(open);
        if (!open && type === "SINGLE") {
          setPickerMode(initialPickerMode || "days");
        }
      }}
    >
      <PopoverTrigger
        disabled={disabled}
        className={cn(
          "flex flex-1 min-w-44 items-center justify-between font-normal px-2 text-sm bg-white",
          !selectedDateRange && "text-muted-foreground",
          className && className
        )}
      >
        {iconPosition === "left" && <Calendar2 className="h-4 w-4 mr-2" />}
        <div className="flex-1 w-full text-left">
          {(
            type === "SINGLE"
              ? date && isValid(date as Date)
              : selectedDateRange?.from
          ) ? (
            <>
              {type === "SINGLE" ? (
                <>
                  {!isIncludeTime
                    ? format(selectedDate, labelDateFormat)
                    : format(selectedDate, "dd MMMM yyyy HH:mm")}
                </>
              ) : (
                <>
                  {selectedDateRange.to ? (
                    <span className="text-darkBrown-default">
                      {`${format(
                        selectedDateRange.from as Date,
                        labelDateFormat
                      )} - ${format(selectedDateRange.to, labelDateFormat)}`}
                    </span>
                  ) : (
                    `${format(
                      selectedDateRange.from as Date,
                      labelDateFormat
                    )}${isShowNoEndDate ? " - No End Date" : ""}`
                  )}
                </>
              )}
            </>
          ) : (
            <span>{placeholder}</span>
          )}
        </div>

        {iconPosition === "right" && <Calendar2 className="h-4 w-4 ml-2" />}
      </PopoverTrigger>

      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          autoPreFill={autoPreFill}
          minDate={minDate}
          maxDate={maxDate}
          initialPickerMode={initialPickerMode}
          pickerMode={type === "SINGLE" ? pickerMode : initialPickerMode}
          onPickerModeChange={type === "SINGLE" ? setPickerMode : undefined}
          disableNavViewChange={disableNavViewChange}
          {...calendarType}
          footer={
            isIncludeTime ? (
              <TimePicker
                timeValue={timeValue}
                setTimeValue={setTimeValue}
                selectedDate={selectedDate}
                setDatePicker={setDatePicker}
              />
            ) : undefined
          }
        />
      </PopoverContent>
    </Popover>
  );
};

export default DateRangePicker;
